/**
 * Alerty.
 *
 * Alert, który nie zamienia się w czyjeś zadanie, jest tylko powiadomieniem —
 * dlatego każdy ma przycisk „zrób z tego zadanie". To jest cały powód, dla
 * którego analityka i zarządzanie pracą mieszkają w jednym systemie.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Card, Chip, Empty, ErrorState, Loading } from "../components/ui";
import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { dateTime, LABELS } from "../lib/format";
import type { Alert } from "../lib/types";

const TONE: Record<string, "info" | "warn" | "crit"> = {
  info: "info",
  warning: "warn",
  critical: "crit",
};

function Glyph({ severity }: { severity: string }) {
  // Ikona plus słowo — status nigdy nie jest przekazywany samym kolorem.
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const };
  return (
    <svg className="alert__icon" viewBox="0 0 20 20" aria-hidden="true">
      {severity === "critical" ? (
        <>
          <path d="M10 2.5 18.5 17H1.5z" {...common} strokeLinejoin="round" />
          <path d="M10 8v4M10 14.5v.01" {...common} />
        </>
      ) : severity === "warning" ? (
        <>
          <circle cx="10" cy="10" r="7.8" {...common} />
          <path d="M10 6v5M10 13.6v.01" {...common} />
        </>
      ) : (
        <>
          <circle cx="10" cy="10" r="7.8" {...common} />
          <path d="M10 9.2v4.6M10 6.6v.01" {...common} />
        </>
      )}
    </svg>
  );
}

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [includeResolved, setIncludeResolved] = useState(false);

  const { scoped, key: demoKey } = useDemo();

  const alerts = useQuery({
    queryKey: ["alerts", includeResolved, demoKey],
    queryFn: () =>
      api.get<{ alerts: Alert[] }>(
        scoped(`/alerts${includeResolved ? "?include_resolved=1" : ""}`),
      ),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["alerts"] });
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };

  const toTask = useMutation({
    mutationFn: (id: string) => api.post(`/alerts/${id}/to-task`),
    onSuccess: invalidate,
  });

  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/alerts/${id}/resolve`),
    onSuccess: invalidate,
  });

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Uwaga</p>
          <h1>Alerty</h1>
          <p className="page-head__sub">
            Ten sam problem w tym samym tygodniu tworzy jeden wpis, nie siedem.
          </p>
        </div>
        <button
          type="button"
          className="btn"
          aria-pressed={includeResolved}
          onClick={() => setIncludeResolved((value) => !value)}
        >
          {includeResolved ? "tylko otwarte" : "pokaż zamknięte"}
        </button>
      </header>

      <Card flush>
        {alerts.isPending ? (
          <Loading />
        ) : alerts.isError ? (
          <ErrorState error={alerts.error} />
        ) : alerts.data?.alerts.length ? (
          <ul>
            {alerts.data.alerts.map((alert) => (
              <li key={alert.id} className="alert">
                <span style={{ color: `var(--${TONE[alert.severity] ?? "info"})` }}>
                  <Glyph severity={alert.severity} />
                </span>

                <div>
                  <p className="alert__title">{alert.title}</p>
                  <p className="alert__msg">{alert.message}</p>
                  <div className="alert__meta">
                    <Chip tone={TONE[alert.severity] ?? "info"}>
                      {LABELS.severity[alert.severity] ?? alert.severity}
                    </Chip>
                    {alert.product_slug && (
                      <Link to={`/produkt/${alert.product_slug}`}>{alert.product_name}</Link>
                    )}
                    <span className="mono">{alert.type}</span>
                    <span>{dateTime(alert.created_at)}</span>
                    {alert.acknowledged_at && <span>przyjęty do realizacji</span>}
                    {alert.resolved_at && <Chip tone="good">zamknięty</Chip>}
                  </div>
                </div>

                {!alert.resolved_at && (
                  <div className="alert__actions">
                    <button
                      type="button"
                      className="btn"
                      disabled={toTask.isPending || Boolean(alert.acknowledged_at)}
                      onClick={() => toTask.mutate(alert.id)}
                      title="Utwórz zadanie na tablicy projektów"
                    >
                      Zrób zadanie
                    </button>
                    <button
                      type="button"
                      className="btn btn--quiet"
                      disabled={resolve.isPending}
                      onClick={() => resolve.mutate(alert.id)}
                    >
                      Zamknij
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty
            title="Cisza w folwarku"
            hint="Żaden próg nie został przekroczony w ostatnim przebiegu."
          />
        )}
      </Card>
    </>
  );
}
